// apps/web/src/adapters/WebNetworkAdapter.ts
// ─────────────────────────────────────────────────────────────────────────
// Online/offline detection for the web. Wraps navigator.onLine + events.
// ─────────────────────────────────────────────────────────────────────────

import type { NetworkAdapter, NetworkListener, NetworkState } from '@nisk/core';

export class WebNetworkAdapter implements NetworkAdapter {
	private listeners = new Set<NetworkListener>();

	constructor() {
		if (typeof window !== 'undefined') {
			window.addEventListener('online', () => this.notify('online'));
			window.addEventListener('offline', () => this.notify('offline'));
		}
	}

	getState(): NetworkState {
		if (typeof navigator === 'undefined') return 'unknown';
		return navigator.onLine ? 'online' : 'offline';
	}

	isOnline(): boolean {
		return this.getState() === 'online';
	}

	subscribe(listener: NetworkListener): () => void {
		this.listeners.add(listener);
		// Fire current state immediately
		listener(this.getState());
		return () => this.listeners.delete(listener);
	}

	async probe(): Promise<NetworkState> {
		// navigator.onLine is often unreliable; for now just return it.
		// Later we can HEAD a tiny endpoint to truly verify reachability.
		return this.getState();
	}

	private notify(state: NetworkState) {
		for (const listener of this.listeners) listener(state);
	}
}

export const network = new WebNetworkAdapter();
